import { RefObject } from 'react';
import { clamp } from 'lodash';

export default function useDraggableResize(
  ref: RefObject<HTMLElement>,
  options: {
    secondaryRef?: RefObject<HTMLElement>;
    direction?: 'vertical' | 'horizontal' | 'both';
    onUpdate?: (value: number) => void;
    minX?: number;
    maxX?: number;
    minY?: number;
    maxY?: number;
  }
) {
  const { direction = 'both', minX = 0, minY = 0, maxX = Infinity, maxY = Infinity, onUpdate } = options;
  const cursor = direction === 'horizontal' ? 'col-resize' : direction === 'vertical' ? 'row-resize' : 'nwse-resize';

  // The initial position (scroll progress and mouse location) when the mouse is pressed down on the element
  let initialPosition = { width: 0, height: 0, mouseX: 0, mouseY: 0 };

  const mouseMoveHandler = (event: { clientX: number; clientY: number }) => {
    if (ref.current) {
      // Calculate differences to see how far the user has moved
      const dx = event.clientX - initialPosition.mouseX;
      const dy = event.clientY - initialPosition.mouseY;

      // Scroll the element according to those differences
      if (direction !== 'horizontal') {
        ref.current.style.height = `${clamp(initialPosition.height + dy, minY, maxY)}px`;
      }
      if (direction !== 'vertical') {
        const newWidth = clamp(initialPosition.width + dx, minX, maxX);
        ref.current.style.width = `${newWidth}px`;
        onUpdate?.(newWidth);
      }

      // If there is some ref that needs to be updated in the same way as
      // main hook ref
      if (options.secondaryRef?.current) {
        // Scroll the element according to those differences
        if (direction !== 'horizontal') {
          options.secondaryRef.current.style.height = `${clamp(initialPosition.height + dy, minY, maxY)}px`;
        }
        if (direction !== 'vertical') {
          const newWidth = clamp(initialPosition.width + dx, minX, maxX);
          options.secondaryRef.current.style.width = `${newWidth}px`;
          onUpdate?.(newWidth);
        }
      }
    }
  };

  const mouseUpHandler = () => {
    // Remove resize cursor after the user is no longer pressing
    if (ref.current) {
      ref.current.style.cursor = '';
    }

    // Remove the event listeners since it is not necessary to track the mouse position anymore
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);
  };

  const onMouseDown = (event: { clientX: number; clientY: number }) => {
    if (ref.current) {
      // Save the position at the moment the user presses down
      initialPosition = {
        height: ref.current.offsetHeight,
        width: ref.current.offsetWidth,
        mouseX: event.clientX,
        mouseY: event.clientY
      };

      // Show a cursor: grabbing style and set user-select: none to avoid highlighting text while dragging
      ref.current.style.cursor = cursor;
      ref.current.style.userSelect = 'none';

      // Add the event listeners that will track the mouse position for the rest of the interaction
      document.addEventListener('mousemove', mouseMoveHandler);
      document.addEventListener('mouseup', mouseUpHandler);
    }
  };

  return { onMouseDown };
}
